import { useState } from 'react';
import { Link } from '@mui/material';
import { useShowContext } from 'framework/show';
import { CreatePageLayout, ShowSection } from 'framework/ui/layout';
import { SuccessMessage, useMessageService } from 'framework/ui/Message';
import { Alert } from 'framework/ui/mui-builder';
import { getProjectManager, useNewProjectUrl, useProjectDataContext } from 'domain/shared/newProject';
import { Link as RouterLink } from 'react-router-dom';
import { ProjectReviewShowData } from './types';
import useProjectReviewService from './useProjectReviewService';
import ProjectReviewBreadcrumbs from './ProjectReviewBreadcrumbs';
import ProjectReviewConfiguration from './configuration/ProjectReviewConfiguration';
import {
  PROJECT_REVIEW_FINALIZED_SUCCESS_MESSAGE,
  PROJECT_REVIEW_FINALIZED_TIMELINE_LINK_LABEL,
  PROJECT_REVIEW_FINALIZE_ERROR_MESSAGE,
} from './constants';
import ResultReview from './result/ResultReview';

const ProjectReviewContent = () => {
  const { data, setData } = useShowContext<ProjectReviewShowData>();
  const { currentProjectReview, latestProjectReview } = data.getData();
  const { finalizeProjectReview } = useProjectReviewService();
  const { getControlsUrl, getProjectTimelineUrl } = useNewProjectUrl();
  const { data: project } = useProjectDataContext();
  const { addMessageAndRedirect } = useMessageService();
  const [hasFinalizeError, setHasFinalizeError] = useState(false);
  const owner = getProjectManager(project);

  const getProjectReviewFinalizedMessage = (timelineUrl: string) => (
    <>
      {PROJECT_REVIEW_FINALIZED_SUCCESS_MESSAGE}{' '}
      <Link component={RouterLink} to={timelineUrl} underline="hover">
        {PROJECT_REVIEW_FINALIZED_TIMELINE_LINK_LABEL}
      </Link>
    </>
  );

  return (
    <CreatePageLayout
      breadcrumbs={<ProjectReviewBreadcrumbs />}
      buttons={<></>}
      hasSeparateSections
      isCancelButtonVisible={false}
    >
      <>
        <ShowSection title="New Project Review" titleVariant="h2">
          <ProjectReviewConfiguration
            hasCurrentReview={!!currentProjectReview}
            latestProjectReview={latestProjectReview}
            onReviewStarted={review => setData?.({ currentProjectReview: review, latestProjectReview })}
          />
        </ShowSection>
        {currentProjectReview && (
          <ShowSection
            title="Review Summary"
            titleVariant="h2"
            justifyContent="space-between"
            actions={
              hasFinalizeError ? (
                <Alert severity="error" onClose={() => setHasFinalizeError(false)}>
                  {PROJECT_REVIEW_FINALIZE_ERROR_MESSAGE}
                </Alert>
              ) : undefined
            }
          >
            <ResultReview
              key={`${currentProjectReview.projectId}-${currentProjectReview.from}-${currentProjectReview.to}`}
              owner={owner}
              review={currentProjectReview}
              onFinalize={payload => {
                setHasFinalizeError(false);

                return finalizeProjectReview(currentProjectReview.projectId, currentProjectReview, payload);
              }}
              onFinalizeError={() => setHasFinalizeError(true)}
              onFinalizeSuccess={() => {
                const controlsUrl = getControlsUrl(project.id);
                const timelineUrl = getProjectTimelineUrl(project.id);

                addMessageAndRedirect(new SuccessMessage(getProjectReviewFinalizedMessage(timelineUrl)), controlsUrl);
              }}
            />
          </ShowSection>
        )}
      </>
    </CreatePageLayout>
  );
};

export default ProjectReviewContent;
